import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Grid, Box, Container, Typography, Button } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Sound, Movie, File, MultiLineBody } from '../../utils/util';
import { CONFIG } from '../../states/settings';
import { api_menu_list } from '../../states/selector';

const ArchiveList = () => {
  const { id, year, month } = useParams();
  const location = useLocation();
  const menuList = useRecoilValue(api_menu_list);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || !year || !month) return;

    (async () => {
      try {
        // console.log('ArchiveList: パラメータ', { id, year, month });
        // console.log('ArchiveList: メニューリスト', menuList);
        
        const targetMenu = menuList.find(group => group.group_name === id);
        // console.log('ArchiveList: 対象メニュー', targetMenu);
        
        if (!targetMenu) {
          console.error('ArchiveList: 対象メニューが見つかりません', id);
          setLoading(false);
          return;
        }

        const url = CONFIG.API.INFO + targetMenu.json_file.split('.')[0] + CONFIG.API.INFO_EX;
        // console.log('ArchiveList: 取得URL', url);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          console.warn(`メニュー ${targetMenu.group_name} のHTTPエラー:`, response.status, response.statusText);
          return;
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn(`メニュー ${targetMenu.group_name} のContent-Typeエラー:`, contentType);
          return;
        }
        
        const json = await response.json();

        const allItems = json.messages || [];
        // console.log('ArchiveList: 全記事数', allItems.length);
        // console.log('ArchiveList: 記事サンプル', allItems.slice(0, 3));

        setItems(allItems);

        // 指定された年月の記事をフィルタリング
        const targetYear = parseInt(year);
        const targetMonth = parseInt(month);
        
        // console.log('ArchiveList: フィルタ条件', { targetYear, targetMonth });
        
        const filtered = allItems.filter(item => {
          const itemDate = new Date(item.sent_at);
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1;
          
          // console.log('ArchiveList: 記事日付チェック', {
          //   sent_at: item.sent_at,
          //   itemYear,
          //   itemMonth,
          //   matches: itemYear === targetYear && itemMonth === targetMonth
          // });
          
          return itemYear === targetYear && itemMonth === targetMonth;
        });

        // 日付の降順でソート（新しい順）
        const sorted = filtered.sort((a, b) => {
          const dateA = new Date(a.sent_at);
          const dateB = new Date(b.sent_at);
          return dateB - dateA; // 降順（新しい順）
        });

        // console.log('ArchiveList: フィルタ結果', filtered.length);
        // console.log('ArchiveList: ソート結果', sorted.length);
        // console.log('ArchiveList: ソートされた記事', sorted);

        setFilteredItems(sorted);
        setLoading(false);
      } catch (error) {
        console.error('アーカイブデータの取得に失敗しました:', error);
        setLoading(false);
      }
    })();
  }, [id, year, month, menuList]);

  // スクロール位置の復元
  useEffect(() => {
    const scrollPosition = location.state?.scroll_position;
    if (scrollPosition && filteredItems.length > 0) {
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 200);
    }
  }, [filteredItems.length, location.state]);

  if (loading) {
    return <div>読み込み中...</div>;
  }

  return (
    <Box className='wrapper' sx={{
      '@media (max-width: 900px)':{
        padding: 2
      },
      padding: 3
    }}>
      <Box 
        mb={3}
        sx={{
          position: 'fixed',
          top: '64px',
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: '#ffffff',
          padding: '16px 24px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '@media (max-width: 900px)': {
            padding: '16px 16px'
          }
        }}
      >
        <Link to={`${CONFIG.PAGE_ROOT_PATH}/`} style={{ textDecoration: 'none' }}>
          <Button 
            startIcon={<ArrowBackRoundedIcon />}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Topページに戻る
          </Button>
        </Link>
        
        <Typography variant="h4" component="h1" gutterBottom>
          {year}年{month}月の記事一覧
        </Typography>
        
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {filteredItems.length}件の記事
        </Typography>
      </Box>

      <Box sx={{ marginTop: '140px' }}>
        <Grid container spacing={2}>
          {filteredItems.map((item) => (
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} key={item.id}>
            <Box
              className={`item_wrapper ${item.is_emergency ? 'emergency' : ''}`}
              pt='.7rem' pr='.7rem' pl='.7rem' pb='1.4rem'
              sx={{
                boxShadow: 2, 
                bgcolor: '#fafafa', 
                alignItems: 'stretch',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                }
              }}
            >
              <Link to={{
                pathname: `${CONFIG.PAGE_ROOT_PATH}/${id}/items/${item.id}`,
                state: {
                  from_url: location.pathname,
                  archive_type: 'individual',
                  scroll_position: window.scrollY
                }
              }} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Typography component="h4" variant="h6" fontWeight='600' mb='1rem' sx={{
                  lineHeight:'1.4',
                  cursor: 'pointer',
                  position: 'relative',
                  padding: '8px 32px 8px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(25, 118, 210, 0.05)',
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  wordBreak: 'break-word',
                  '&:hover': {
                    color: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    border: '1px solid #1976d2',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&::after': {
                      content: '"👆"',
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '14px',
                      opacity: 0.7,
                      pointerEvents: 'none'
                    }
                  },
                  '&::before': {
                    content: '"📖"',
                    marginRight: '8px',
                    fontSize: '16px'
                  }
                }}>
                  {item.title}
                </Typography>
              </Link>

              <Grid className='item_list_other' container mb='1.5rem'>
                <p className='sender_name'>{item.sender_name}</p>
                <time className='time_c1' dateTime={format(new Date(item.sent_at),'yyyy-MM-dd HH:mm')}>
                  {format(new Date(item.sent_at),'yyyy-MM-dd HH:mm')}
                </time>
              </Grid>

              <div onClick={(e) => e.stopPropagation()}>
                <Sound item={item.sound_file ? item.sound_file : (item.generated_sound_file ? item.generated_sound_file : null)} />
              </div>

              <Typography className='mabiku' component="p" variant="h7" mb='1.2rem' sx={{ 
                fontSize: '.9rem',
                lineHeight: '1.6'
              }}>
                <MultiLineBody body={item.body} />
              </Typography>

              <div onClick={(e) => e.stopPropagation()}>
                <Movie item={item.movie_url} />
                <File item={item.file} />
              </div>
            </Box>
          </Grid>
          ))}
        </Grid>
      </Box>

      {filteredItems.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            該当月の記事はありません
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ArchiveList;
